import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material'
import supabase from '../supabaseClient'

function WaitlistConversionModal({ 
  open, 
  waitlistEntry, 
  onConvert, 
  onCancel 
}) {
  const [selectedClasses, setSelectedClasses] = useState([])
  const [classes, setClasses] = useState([])
  const [existingStudentClasses, setExistingStudentClasses] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Extract desired shifts from waitlist entry (handles both array and single shift formats)
  const desiredShifts = useMemo(() => {
    return waitlistEntry?.desired_shifts || 
           (waitlistEntry?.desired_shift ? [waitlistEntry.desired_shift] : [])
  }, [waitlistEntry])

  const isExistingStudent = !!waitlistEntry?.student_id

  const fetchExistingStudentClasses = useCallback(async () => {
    if (!waitlistEntry?.student_id) return
    
    try {
      const { data, error } = await supabase
        .from('students')
        .select('class_ids')
        .eq('id', waitlistEntry.student_id)
        .single()

      if (error) throw error
      
      if (data?.class_ids && data.class_ids.length > 0) {
        // Fetch class details
        const { data: classData, error: classError } = await supabase
          .from('classes')
          .select('id, name, shift')
          .in('id', data.class_ids)

        if (!classError && classData) {
          setExistingStudentClasses(classData)
        }
      }
    } catch (error) {
      console.error('Error fetching existing student classes:', error)
    }
  }, [waitlistEntry?.student_id])

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      
      // Build query: classes with desired shifts OR no shift (global classes)
      let query = supabase
        .from('classes')
        .select('id, name, shift, teacher_id')
        .eq('is_active', true)
      
      // If we have desired shifts, include classes with those shifts OR no shift (global classes)
      // If no desired shifts, only get global classes (no shift)
      if (desiredShifts.length > 0) {
        // Build OR condition: (shift IN (shift1, shift2, ...) OR shift IS NULL)
        query = query.or(`shift.in.(${desiredShifts.join(',')}),shift.is.null`)
      } else {
        // Only get global classes (no shift)
        query = query.is('shift', null)
      }
      
      const { data, error } = await query.order('name')
      
      if (error) throw error
      setClasses(data || [])
      
    } catch (error) {
      console.error('Error fetching classes:', error)
      setError('Failed to load classes')
    } finally {
      setLoading(false)
    }
  }, [desiredShifts])

  useEffect(() => {
    if (open && waitlistEntry) {
      setSelectedClasses([])
      setError('')
      fetchData()
      if (waitlistEntry.student_id) {
        fetchExistingStudentClasses()
      }
    }
  }, [open, waitlistEntry, fetchData, fetchExistingStudentClasses])

  const handleConvert = async () => {
    if (selectedClasses.length === 0) {
      setError('Please select at least one class')
      return
    }

    await onConvert(waitlistEntry, selectedClasses)
  }

  // Get set of existing class IDs for quick lookup
  const existingClassIds = useMemo(() => {
    return new Set(existingStudentClasses.map(cls => cls.id))
  }, [existingStudentClasses])

  // Check if a class is already enrolled
  const isAlreadyEnrolled = useCallback((classId) => {
    return isExistingStudent && existingClassIds.has(classId)
  }, [isExistingStudent, existingClassIds])

  // Get selected class names for display
  const getSelectedClassNames = (selected) => {
    return classes
      .filter(c => selected.includes(c.id))
      .map(c => c.name)
      .join(', ')
  }

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        {isExistingStudent ? 'Assign Classes to Student' : 'Convert Waitlist Entry to Student'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Student Info Summary */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
              Student Information
            </Typography>
            <Typography variant="body2">
              <strong>Name:</strong> {waitlistEntry?.first_name} {waitlistEntry?.middle_name} {waitlistEntry?.last_name}
            </Typography>
            {waitlistEntry?.gender && (
              <Typography variant="body2">
                <strong>Gender:</strong> {waitlistEntry.gender}
              </Typography>
            )}
            {waitlistEntry?.date_of_birth && (
              <Typography variant="body2">
                <strong>Date of Birth:</strong> {waitlistEntry.date_of_birth}
              </Typography>
            )}
            {desiredShifts.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" component="span">
                  <strong>Desired Shifts:</strong>{' '}
                </Typography>
                {desiredShifts.filter(Boolean).map(shift => (
                  <Chip key={shift} label={shift} size="small" sx={{ ml: 0.5, mb: 0.5 }} />
                ))}
              </Box>
            )}
            {isExistingStudent && existingStudentClasses.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" component="span">
                  <strong>Current Classes:</strong>{' '}
                </Typography>
                {existingStudentClasses.map(cls => (
                  <Chip key={cls.id} label={cls.name} size="small" color="primary" sx={{ ml: 0.5, mb: 0.5 }} />
                ))}
              </Box>
            )}
          </Box>

          {/* Class Selection */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <FormControl fullWidth>
              <InputLabel>Select Classes</InputLabel>
              <Select
                multiple
                value={selectedClasses}
                onChange={(e) => setSelectedClasses(e.target.value)}
                input={<OutlinedInput label="Select Classes" />}
                renderValue={(selected) => getSelectedClassNames(selected)}
              >
                {classes.length > 0 ? (
                  classes.map((cls) => {
                    const alreadyEnrolled = isAlreadyEnrolled(cls.id)
                    return (
                      <MenuItem 
                        key={cls.id} 
                        value={cls.id}
                        disabled={alreadyEnrolled}
                        sx={alreadyEnrolled ? { opacity: 0.5 } : {}}
                      >
                        <Checkbox checked={selectedClasses.includes(cls.id)} disabled={alreadyEnrolled} />
                        <ListItemText 
                          primary={cls.name}
                          secondary={alreadyEnrolled ? 'Already enrolled' : (cls.shift || 'Global Class')}
                        />
                      </MenuItem>
                    )
                  })
                ) : (
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">
                      No classes found
                    </Typography>
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button 
          onClick={handleConvert} 
          variant="contained"
          disabled={loading || selectedClasses.length === 0}
        >
          {loading ? <CircularProgress size={24} /> : 'Convert & Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default WaitlistConversionModal

